<!--
File: EditSectionForm.vue
Description: form for adding/editing a single road section.
-->
<template>
  <modal v-if="eligible" :title="screenTitle" :form="formName" :errCount="errors.count()"
    :hasUnsavedChanges="hasUnsavedChanges" @close="$emit('close')" @save="validate" class="section-edit-modal">

    <template slot='body'>
      <VueTabs>

        <!-- Tab 1 | Section overview -->
        <v-tab id="tab0" :title="$t('tabs.section_overview')">
          <div class="md-layout">

            <RegionsDropdown :class="getClass('so_fk_region', 35)" v-model="so_fk_region" :isRequired="true" 
              data-vv-name="so_fk_region" v-validate="modelValidations.so_fk_region" />

            <DepsDropdown :class="getClass('so_fk_deu', 30)" :label="$t('road_network.dep')" v-model="so_fk_deu"
              data-vv-name="so_fk_deu" :v-validate="modelValidations.so_fk_deu" :isRequired="true"
              :regionId="so_fk_region" />

            <RoadsDropdown :class="getClass('so_fk_road', 35)" :label="$t('road_network.road')" v-model="so_fk_road"
              data-vv-name="so_fk_road" v-validate="modelValidations.so_fk_road" :isRequired="true"
              :regionId="so_fk_region" />

          </div>
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-25">
              <md-field
                :class="[{ 'md-valid': !errors.has('so_section_key') }, { 'md-error': errors.has('so_section_key') }]">
                <label>{{ $t('road_network.section_key') }}</label>
                <md-input v-model="so_section_key" type="text" data-vv-name="so_section_key" required
                  v-validate="modelValidations.so_section_key" />
              </md-field>

            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">
              <md-field
                :class="[{ 'md-valid': !errors.has('so_section_number') }, { 'md-error': errors.has('so_section_number') }]">
                <label>{{ $t('road_network.section_number') }}</label>
                <md-input v-model="so_section_number" type="number" data-vv-name="so_section_number" required
                  v-validate="modelValidations.so_section_number" />
              </md-field>
            </div>
          </div>

          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-50">
              <md-field
                :class="[{ 'md-valid': !errors.has('so_section_description') }, { 'md-error': errors.has('so_section_description') }]">
                <label>{{ $t('stdCols.name_ru') }}</label>
                <md-input v-model="so_section_description" type="text" data-vv-name="so_section_description" required
                  v-validate="modelValidations.so_section_description" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-50">
              <md-field
                :class="[{ 'md-valid': !errors.has('so_section_description_en') }, { 'md-error': errors.has('so_section_description_en') }]">
                <label>{{ $t('stdCols.name_en') }}</label>
                <md-input v-model="so_section_description_en" type="text" data-vv-name="so_section_description_en"
                  required v-validate="modelValidations.so_section_description_en" />
              </md-field>
            </div>
          </div>

          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field
                :class="[{ 'md-valid': !errors.has('so_villages') }, { 'md-error': errors.has('so_villages') }]">
                <label>{{ $t('inventory.villages') }}</label>
                <md-input v-model="so_villages" type="number" data-vv-name="so_villages" required
                  v-validate="modelValidations.so_villages" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field :class="[{ 'md-valid': !errors.has('so_aadt') }, { 'md-error': errors.has('so_aadt') }]">
                <label>{{ $t('inventory.aadt') }}</label>
                <md-input v-model="so_aadt" type="number" data-vv-name="so_aadt" required
                  v-validate="modelValidations.so_aadt" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field :class="[{ 'md-valid': !errors.has('fclass') }, { 'md-error': errors.has('fclass') }]">
                <label>{{ $t('inventory.fclass') }}</label>
                <md-input v-model="so_fclass" data-vv-name="fclass" type="number" min=0 max=3 required
                  v-validate="modelValidations.so_fclass" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field
                :class="[{ 'md-valid': !errors.has('so_black_spots') }, { 'md-error': errors.has('so_black_spots') }]">
                <label>{{ $t('condition.black_spots') }}</label>
                <md-input v-model="so_black_spots" data-vv-name="so_black_spots" type="number" min=0 max=9 required
                  v-validate="modelValidations.so_black_spots" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-20">
              <label>
                <input type="checkbox" v-model="so_social_importance" true-value="1" false-value="0" />
                {{ $t('inventory.social_importance') }}
              </label>
            </div>
          </div>
        </v-tab>


        <!-- Tab 2 | Section location -->
        <v-tab id="tab1" :title="$t('tabs.section_location')">
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-50">
              <md-field>
                <label>{{ $t('inventory.start_place') }}</label>
                <md-input v-model="sl_start_place" type="text" />
              </md-field>
            </div>
          </div>
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-50">
              <md-field>
                <label>{{ $t('inventory.end_place') }}</label>
                <md-input v-model="sl_end_place" type="text" />
              </md-field>
            </div>
          </div>
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-45">
              <md-field
                :class="[{ 'md-valid': !errors.has('sl_start_distance_m') }, { 'md-error': errors.has('sl_start_distance_m') }]">
                <label>{{ $t('inventory.start_distance_m') }}</label>
                <md-input v-model="sl_start_distance_m" type="number" data-vv-name="sl_start_distance_m" required />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-45">
              <md-field
                :class="[{ 'md-valid': !errors.has('sl_end_distance_m') }, { 'md-error': errors.has('sl_end_distance_m') }]">
                <label>{{ $t('inventory.end_distance_m') }}</label>
                <md-input v-model="sl_end_distance_m" type="number" required />
              </md-field>
            </div>
          </div>
        </v-tab>


        <!-- Tab 3 | Section geometry -->
        <v-tab id="tab2" :title="$t('tabs.section_geometry')">
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field>
                <label>{{ $t('inventory.start_distance_m') }}</label>
                <md-input v-model="sg_start_distance_m" type="Number" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field>
                <label>{{ $t('inventory.end_distance_m') }}</label>
                <md-input v-model="sg_end_distance_m" type="Number" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">

              <RdbLookupDropdownById :class="getClass('sg_surface_type')" :label="$t('condition.surface_type_desc')"
                v-model="sg_surface_type" :lookupType="'surface_type'" :addEmptyLine="false" 
                data-vv-name="sg_surface_type" v-validate="modelValidations.sg_surface_type"
                @input='onPavementTypeChange' />

            </div>
            <div class="md-layout-item md-small-size-100 md-size-35">

              <RdbLookupDropdownById :class="getClass('sg_terrain_character')" :label="$t('condition.terrain_type_desc')" 
                v-model="sg_terrain_character" :lookupType="'terrain_type'" :addEmptyLine="true"  
                data-vv-name="sg_terrain_character" v-validate="modelValidations.sg_terrain_character"
                @input='onTerrainTypeChange' />

            </div>
          </div>

          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-25">
              <md-field>
                <label>{{ $t('condition.right_shoulder_width_m') }}</label>
                <md-input v-model="sg_right_shoulder_width_m" type="Number" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">

              <RdbLookupDropdownById :class="getClass('sg_right_shoulder_type')" :label="$t('condition.right_shoulder_type_desc')" 
                v-model="sg_right_shoulder_type" :lookupType="'surface_type'" :addEmptyLine="true"  
                data-vv-name="sg_right_shoulder_type" v-validate="modelValidations.sg_right_shoulder_type"
                @input='onRightShoulderTypeChange' />
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">
              <md-field>
                <label>{{ $t('condition.left_shoulder_width_m') }}</label>
                <md-input v-model="sg_left_shoulder_width_m" type="Number" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">
              <RdbLookupDropdownById :class="getClass('sg_left_shoulder_type')" :label="$t('condition.left_shoulder_type_desc')" 
                v-model="sg_left_shoulder_type" :lookupType="'surface_type'" :addEmptyLine="false" 
                data-vv-name="sg_left_shoulder_type" v-validate="modelValidations.sg_left_shoulder_type"
                @input='onLeftShoulderTypeChange' />
            </div>
          </div>

          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-25">

              <RdbLookupDropdownById :class="getClass('sg_land_use_type')" :label="$t('condition.land_use_type_desc')" 
                v-model="sg_land_use_type" :lookupType="'land_use'" :addEmptyLine="false"  
                data-vv-name="sg_land_use_type" v-validate="modelValidations.sg_land_use_type"
                @input='onLandUseTypeChange'  />
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">
              <md-field>
                <label>{{ $t('condition.median_width_m') }}</label>
                <md-input v-model="sg_median_width" type="Number" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">
              <BaseDropdown :label="$t('road_network.nr_of_lanes')" v-model="sg_number_of_lanes"
                :dropdownId='"number_of_lanes"' :items="lanesList" :displayField="'description'"
                :valueField="'description'" @onChange='onNumberOfLanesChange' />
            </div>
            <div class="md-layout-item md-small-size-100 md-size-25">
              <md-field>
                <label>{{ $t('label.carriageway_width') + $t('label.meters') }}</label>
                <md-input v-model="sg_carriageway_width_m" type="Number" />
              </md-field>
            </div>
          </div>

          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-30">
              <md-field>
                <label>{{ $t('inventory.direction') }}</label>
                <md-input v-model="sg_direction" type="text" />
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-30">
              <md-field>
                <label>{{ $t('label.dual') }}</label>
                <md-input v-model="sg_dual" type="Number" />
              </md-field>
            </div>
          </div>
        </v-tab>

      </VueTabs>

    </template>
  </modal>
</template>
<script>
import { VueTabs, VTab } from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css'
import { mapState, mapActions } from 'vuex'
import { Modal, RegionsDropdown, DepsDropdown, RoadsDropdown, BaseDropdown, RdbLookupDropdown, RdbLookupDropdownById } from '@/pages/Components'
import { savedMessage } from '@/mixins/messagesMixin'
import permissions from "@/mixins/permissionsMixin"

export default {
  name: 'section-edit-form',
  mixins: [permissions],

  data() {
    return {
      formName: 'SectionEditForm',
      eligible: false,

      // Form fields

      // Tab 1 - 'Section Overview'
      so_section_id: null,
      so_fk_region: null,
      so_fk_deu: null,
      so_fk_road: null,
      so_section_key: null,
      so_section_number: null,
      so_section_description: null,
      so_section_description_en: null,
      so_villages: null,
      so_aadt: null,
      so_fclass: null,
      so_black_spots: null,
      so_social_importance: null,

      // Tab 2 - 'Section Location'
      sl_start_place: null,
      sl_end_place: null,
      sl_start_distance_m: null,
      sl_end_distance_m: null,

      // Tab 3 - 'Section Geometry'
      sg_geometry_id: null,
      sg_fk_section: null,
      sg_start_distance_m: null,
      sg_end_distance_m: null,
      sg_surface_type: null,
      sg_terrain_character: null,
      sg_right_shoulder_width_m: null,
      sg_right_shoulder_type: null,
      sg_left_shoulder_width_m: null,
      sg_left_shoulder_type: null,
      sg_land_use_type: null,
      sg_median_width: null,
      sg_number_of_lanes: null,
      sg_carriageway_width_m: null,
      sg_direction: null,
      sg_dual: null,

      initialFormState: null,
      initialFormStateGeom: null,

      modelValidations: {

        // Tab 1 - 'Section Overview'
        so_fk_region: { required: true, numeric: true, min_value: 1 },
        so_fk_deu: { required: true, numeric: true, min_value: 1 },
        so_fk_road: { required: true, numeric: true, min_value: 1 },
        so_section_key: { required: true, min: 3 },
        so_section_number: { required: true, numeric: true },
        so_section_description: { required: true, min: 3 },
        so_section_description_en: { required: true, min: 3 },
        so_villages: { required: true, numeric: true, min_value: 0, max_value: 49 },
        so_aadt: { required: true, decimal: true },
        so_fclass: { required: true, numeric: true, min_value: 0, max_value: 3 },
        so_black_spots: { required: true, numeric: true, min_value: 0, max_value: 9 },
        so_social_importance: { required: false, numeric: true, min_value: 0, max_value: 1 },

        // Tab 2 - 'Section Location'
        sl_start_distance_m: { required: true, numeric: true, min_value: 0 },
        sl_end_distance_m: { required: true, numeric: true, min_value: 100 },

        // Tab 3 - 'Section Geometry' - Lookup fields (optional)
        sg_surface_type: { required: false, numeric: true, min_value: 1 },
        sg_terrain_character: { required: false, numeric: true, min_value: 1 },
        sg_right_shoulder_type: { required: false, numeric: true, min_value: 1 },
        sg_left_shoulder_type: { required: false, numeric: true, min_value: 1 },
        sg_land_use_type: { required: false, numeric: true, min_value: 1 },

      },

      lanesList: [
        { description: 1 },
        { description: 2 },
        { description: 3 },
        { description: 4 },
        { description: 5 },
        { description: 6 }
      ],
    }
  },

  props: {
    itemId: null
  },

  components: {
    VueTabs,
    VTab,
    Modal,
    RegionsDropdown,
    DepsDropdown,
    RoadsDropdown,
    RdbLookupDropdown,
    RdbLookupDropdownById,
    BaseDropdown,
  },

  async mounted() {


    this.eligible = true;

    // Check if we are eligible to view the form
    // this.eligible = await this.checkIfScreenAllowed();
    // if (!this.eligible) {
    //     this.$emit('close');
    //     return;
    // };

    if (this.itemId) { // Load data on existing Traffic Site

      // const theSite = await this.loadTrafficSite(this.itemId);
      const section = await this.loadSection(this.itemId);

      // Tab 1 - 'Section Overview'
      this.so_section_id = section.section_id;
      this.so_fk_region = section.fk_region;
      this.so_fk_deu = section.fk_deu;
      this.so_fk_road = section.fk_road;
      this.so_section_key = section.section_key;
      this.so_section_number = section.section_number;
      this.so_section_description = section.section_description;
      this.so_section_description_en = section.section_description_en;
      this.so_villages = section.villages;
      this.so_aadt = section.aadt;
      this.so_fclass = section.fclass;
      this.so_black_spots = section.black_spots;
      this.so_social_importance = section.social_importance;

      // Tab 2 - 'Section Location'
      this.sl_start_place = section.start_place,
        this.sl_end_place = section.end_place,
        this.sl_start_distance_m = section.start_distance_m,
        this.sl_end_distance_m = section.end_distance_m


      await this.loadGeometry({ section_id: this.itemId })

      if (this.sectionGeometryList.length > 0) {
        const geometry = JSON.parse(JSON.stringify(this.sectionGeometryList[0]));

        // Validate lookup field values
        const validatedValues = await this.validateLookupFields(geometry);

        // Tab 3 - 'Section Geometry'
        this.sg_geometry_id = geometry.geometry_id,
        this.sg_fk_section = geometry.fk_section,
        this.sg_start_distance_m = geometry.start_distance_m,
        this.sg_end_distance_m = geometry.end_distance_m,
        this.sg_surface_type = validatedValues.surface_type,
        this.sg_terrain_character = validatedValues.terrain_character,
        this.sg_right_shoulder_width_m = geometry.right_shoulder_width_m,
        this.sg_right_shoulder_type = validatedValues.right_shoulder_type,
        this.sg_left_shoulder_width_m = geometry.left_shoulder_width_m,
        this.sg_left_shoulder_type = validatedValues.left_shoulder_type,
        this.sg_land_use_type = validatedValues.land_use_type,
        this.sg_median_width = geometry.median_width,
        this.sg_number_of_lanes = geometry.number_of_lanes,
        this.sg_carriageway_width_m = geometry.carriageway_width_m,
        this.sg_direction = geometry.direction,
        this.sg_dual = geometry.dual;



        // delete this.geometry.section;
      }
      else {
        this.sg_fk_section = Number(this.itemId)
        this.sg_start_distance_m = Number(section.start_distance_m)
        this.sg_end_distance_m = Number(section.end_distance_m)
      }

    }

    this.$nextTick(async () => {

      this.initialFormState = this.getCurrentState;
      this.initialFormStateGeom = this.getCurrentStateGeom;


      await this.$validator.validateAll();


    });


  },

  methods: {
    ...mapActions({
      loadSection: 'LOAD_SECTION_BY_ID',
      loadGeometry: 'LOAD_SECTION_GEOMETRY_LIST',
      addItem: 'ADD_NEW_SECTION',
      editItem: 'UPDATE_SECTION',
      addGeometry: 'ADD_NEW_SECTION_GEOMETRY',
      editGeometry: 'UPDATE_SECTION_GEOMETRY',
      highlightRow: 'HIGHLIGHT_ROW',
      loadLookupData: 'ReferenceData/LOAD_RDB_LOOKUP_BY_ID',
    }),

    // Validate lookup field values against actual lookup table IDs
    async validateLookupFields(geometry) {
      const lookupTypes = [
        { type: 'surface_type', field: 'surface_type', value: geometry.surface_type },
        { type: 'terrain_type', field: 'terrain_character', value: geometry.terrain_character },
        { type: 'surface_type', field: 'right_shoulder_type', value: geometry.right_shoulder_type },
        { type: 'surface_type', field: 'left_shoulder_type', value: geometry.left_shoulder_type },
        { type: 'land_use', field: 'land_use_type', value: geometry.land_use_type }
      ];

      const validatedValues = {};

      for (const lookup of lookupTypes) {
        if (lookup.value !== null && lookup.value !== undefined && lookup.value !== '') {
          try {
            // Load lookup data for this type
            const lookupData = await this.loadLookupData(lookup.type);
            const validIds = lookupData.map(item => item.id);
            
            // Convert value to integer for comparison
            const numValue = parseInt(lookup.value);
            
            if (!isNaN(numValue) && validIds.includes(numValue)) {
              // Valid ID - use it
              validatedValues[lookup.field] = numValue;
            } else {
              // Invalid value - log error and set to null
              console.log(`⚠️ Invalid ${lookup.field} value for section ${geometry.fk_section}: expected lookup ID from [${validIds.join(', ')}], got: "${lookup.value}"`);
              validatedValues[lookup.field] = null;
            }
          } catch (error) {
            console.log(`❌ Error validating ${lookup.field}: ${error.message}`);
            validatedValues[lookup.field] = null;
          }
        } else {
          validatedValues[lookup.field] = null;
        }
      }

      return validatedValues;
    },

    async validate() {


      const isValid = await this.$validator.validateAll();
      if (!isValid) return;


      const theItem = this.getCurrentState;
      const theItemGeom = this.getCurrentStateGeom;



      let errDesc = '';
      let newId;

      const action = !this.itemId ? this.addItem : this.editItem;
      const actionGeom = !this.itemId ? this.addGeometry : this.editGeometry;

      const payload = !this.itemId ? theItem : { id: this.itemId, theItem: theItem }
      const payloadGeom = !this.itemId ? theItemGeom : { id: this.sg_geometry_id, theItem: theItemGeom }
      
      // delete theItemGeom.geometry_id
      
      try {
        const res = await action(payload)
        newId = res?.section_id;
        // Add section id to the geometry object

        if (!this.itemId)
          theItemGeom.fk_section = Number(newId)

        try {
          await actionGeom(payloadGeom)
        } catch (errGeom) {
          errDesc += 'Geom: ' + errGeom.message
        }
      } catch (err) {
        errDesc += err.message || this.$t('errors.unknown_error')
      }

      this.$nextTick(() => this.$validator.reset());
      this.$emit('close');

      await savedMessage(errDesc, this.$t('road_network.section'), this.so_section_description);
      this.highlightRow(newId);

    },

    onPavementTypeChange(value) {
      console.log('Pavement type changed:', value)
      this.sg_surface_type = value
    },

    onTerrainTypeChange(value) {
      this.sg_terrain_character = value
    },

    onNumberOfLanesChange(value) {
      this.sg_number_of_lanes = value
    },

    onLandUseTypeChange(value) {
      this.sg_land_use_type = value
    },

    onRightShoulderTypeChange(value) {
      this.sg_right_shoulder_type = value
    }, 
    
    onLeftShoulderTypeChange(value) {
      this.sg_left_shoulder_type = value
    },
  },

  computed: {
    ...mapState({
      sectionGeometryList: (state) => state.RoadNetwork.section_geometry_list
    }),

    screenTitle() {
      return this.itemId ? this.$t('screen_titles.section_upd') : this.$t('screen_titles.section_add');
    },

    getCurrentState() {
      return {
        // Tab 1 - 'Section Overview'
        section_id: this.so_section_id,
        fk_region: this.so_fk_region,
        fk_deu: this.so_fk_deu,
        fk_road: this.so_fk_road,
        section_key: this.so_section_key,
        section_number: parseInt(this.so_section_number),
        section_description: this.so_section_description,
        section_description_en: this.so_section_description_en,
        villages: parseInt(this.so_villages),
        aadt: parseInt(this.so_aadt),
        fclass: parseInt(this.so_fclass),
        black_spots: parseInt(this.so_black_spots),
        social_importance: parseInt(this.so_social_importance),

        // Tab 2 - 'Section Location'
        start_place: this.sl_start_place,
        end_place: this.sl_end_place,
        start_distance_m: parseInt(this.sl_start_distance_m),
        end_distance_m: parseInt(this.sl_end_distance_m)
      };
    },

    getCurrentStateGeom() {
      return {
        // Tab 3 - 'Section Geometry'
        fk_section: this.sg_fk_section,
        geometry_id: this.sg_geometry_id,
        start_distance_m: parseInt(this.sg_start_distance_m),
        end_distance_m: parseInt(this.sg_end_distance_m),
        surface_type: this.sg_surface_type,
        terrain_character:  parseInt(this.sg_terrain_character),
        right_shoulder_width_m: parseInt(this.sg_right_shoulder_width_m),
        right_shoulder_type: this.sg_right_shoulder_type,
        left_shoulder_width_m: parseInt(this.sg_left_shoulder_width_m),
        left_shoulder_type: this.sg_left_shoulder_type,
        land_use_type: this.sg_land_use_type,
        median_width: parseInt(this.sg_median_width),
        number_of_lanes: this.sg_number_of_lanes,
        carriageway_width_m: parseInt(this.sg_carriageway_width_m),
        direction: parseInt(this.sg_direction),
        dual: parseInt(this.sg_dual)
      };
    },

    hasUnsavedChanges() {
      if (!this.initialFormState || !this.initialFormStateGeom) return false;

      return this.initialFormState.so_fk_region !== this.so_fk_region ||
        this.initialFormState.so_fk_deu !== this.so_fk_deu ||
        this.initialFormState.so_fk_road !== this.so_fk_road ||
        this.initialFormState.so_section_key !== this.so_section_key ||
        this.initialFormState.so_section_number !== this.so_section_number ||
        this.initialFormState.so_section_description !== this.so_section_description ||
        this.initialFormState.so_section_description_en !== this.so_section_description_en ||
        this.initialFormState.so_villages !== this.so_villages ||
        this.initialFormState.so_aadt !== this.so_aadt ||
        this.initialFormState.so_fclass !== this.so_fclass ||
        this.initialFormState.so_black_spots !== this.so_black_spots ||
        this.initialFormState.so_social_importance !== this.so_social_importance ||

        this.initialFormState.sl_start_place !== this.sl_start_place ||
        this.initialFormState.sl_end_place !== this.sl_end_place ||
        this.initialFormState.sl_start_distance_m !== this.sl_start_distance_m ||
        this.initialFormState.sl_end_distance_m !== this.sl_end_distance_m ||

        this.initialFormStateGeom.sg_start_distance_m !== this.sg_start_distance_m ||
        this.initialFormStateGeom.sg_end_distance_m !== this.sg_end_distance_m ||
        this.initialFormStateGeom.sg_surface_type !== this.sg_surface_type ||
        this.initialFormStateGeom.sg_terrain_character !== this.sg_terrain_character ||
        this.initialFormStateGeom.sg_right_shoulder_width_m !== this.sg_right_shoulder_width_m ||
        this.initialFormStateGeom.sg_right_shoulder_type !== this.sg_right_shoulder_type ||
        this.initialFormStateGeom.sg_left_shoulder_width_m !== this.sg_left_shoulder_width_m ||
        this.initialFormStateGeom.sg_left_shoulder_type !== this.sg_left_shoulder_type ||
        this.initialFormStateGeom.sg_land_use_type !== this.sg_land_use_type ||
        this.initialFormStateGeom.sg_median_width !== this.sg_median_width ||
        this.initialFormStateGeom.sg_number_of_lanes !== this.sg_number_of_lanes ||
        this.initialFormStateGeom.sg_carriageway_width_m !== this.sg_carriageway_width_m ||
        this.initialFormStateGeom.sg_direction !== this.sg_direction ||
        this.initialFormStateGeom.sg_dual !== this.sg_dual;

    }
  }
}
</script>
<style>
.md-card {
  margin: 0px 0;
}

.md-button+.md-button {
  margin-left: 10px;
}

.modal-container {
  max-width: 700px;
}

.section-edit-modal .modal-container {
  max-width: 1000px;
  min-width: 900px;
}

.vue-tabs .nav-tabs>li.active>a,
.vue-tabs .nav-tabs>li.active>a:hover,
.vue-tabs .nav-tabs>li.active>a:focus {
  background-color: #4caf50;
}
</style>